import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { useEffect } from 'react';
import { useCloseModal, useOpenModal } from 'src/common/components/next-modal';
import { TABLE_CELL_MENU } from 'src/common/const';
import { TableCellMenu } from 'src/editor/editor/plugin/table/table-cell-menu';
import { useOpenBlockRightMenuList } from 'src/editor/editor/uikit/hover-menu/block-menu-list/hook';
import { cursorIsInAside } from 'src/hooks/page/use-dnd/helper';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import { $appUiStateCache } from 'src/services/app';
import { getEditorKeyFromElement } from 'src/utils/editor-utils';
import { getDynamicPageId } from 'src/utils/getPageId';
import { idsToSelectedBlocks } from 'src/utils/select-block-util';
import { elementToGetBoundingClientRect, getVirtualElement } from 'src/utils/virtualElement';
import { getPermissions } from '../../share/use-permissions';

export const useHandleContextMenu = (readonly?: boolean) => {
  // 由于这个方法不在pageContent上，因此不能用useIsInRight来判断在哪
  const closeModal = useCloseModal();
  const openBlockRightMenuList = useOpenBlockRightMenuList();
  const openModal = useOpenModal();

  useEffect(() => {
    /** 当光标在富文本上的时候，弹出系统菜单，跟N/L一样，因为系统支持粘贴功能,已跟PM沟通过 */
    const handleContextMenu = (event: MouseEvent | React.MouseEvent<HTMLElement>) => {
      if ((event.target as HTMLElement).closest('[data-disable-contextmenu]')) return;

      if (readonly) return;
      // 鼠标在目录区域先禁止弹窗
      if (cursorIsInAside(event.clientX)) return;

      if ($appUiStateCache.$blockMenuListId) {
        closeModal($appUiStateCache.$blockMenuListId);
      } else {
        const node = (event.target as HTMLElement).closest('[data-block-id]');
        if (!(node instanceof HTMLElement)) {
          return;
        }
        const isCursorInEditor = !!getEditorKeyFromElement(document.activeElement);
        if (isCursorInEditor && cache.ui.selectedBlocks.length === 0) {
          // 弹出系统默认的
          return;
        }

        const { blockId, viewId, syncId, mindMapId } = node.dataset;
        if (blockId) {
          const block = cache.blocks[blockId];

          if (mindMapId) return; // 导图自己处理

          if (blockId === getDynamicPageId()) return;

          if (getPermissions(block?.parentId ?? '').role === PermissionRole.READER) return;
          event.preventDefault();

          const virtualElement = getVirtualElement(
            event as React.MouseEvent<HTMLElement>
          ) as HTMLElement;

          if (block?.type === BlockType.TABLE) {
            openModal.dropdown({
              modalId: TABLE_CELL_MENU,
              popcorn: elementToGetBoundingClientRect(virtualElement),
              placement: 'bottom-start',
              offset: [16, -2],
              closeNoAnimate: true,
              content({ onCloseModal }) {
                return (
                  <TableCellMenu
                    type="contextmenu"
                    isFirstRow={false}
                    isFirstColumn={false}
                    tableId={blockId}
                    onCloseModal={() => onCloseModal()}
                  />
                );
              },
            });

            return;
          }

          if (
            cache.ui.selectedBlocks.findIndex((v) => v.blockId === blockId) === -1 &&
            !cache.ui.selectedCells.some((cell) => cell.recordId === blockId)
          ) {
            // 如果不是右键选中的cell或者record，则替换成当前右键对应的record
            dispatch(uiActions.updateSelectBlocks([{ blockId, viewId, syncId }]));
          } else if (cache.ui.selectedCells.length > 0) {
            // 如果是选中了多个cell后又键，则转换成选中多个record，like notion
            const selectBlocks = new Set<string>();
            cache.ui.selectedCells.forEach((cell) => {
              selectBlocks.add(cell.recordId);
            });
            dispatch(
              uiActions.updateSelectBlocks(idsToSelectedBlocks([...selectBlocks], viewId, syncId))
            );
          }

          openBlockRightMenuList({
            blockId,
            blockNode: { ...virtualElement, dataset: node.dataset },
            noSelect: true,
            placement: 'bottom-start',
            offset: [10, 10],
          });
        } else {
          dispatch(uiActions.updateSelectBlocks([]));
        }
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [closeModal, openBlockRightMenuList, openModal, readonly]);
};
